import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Container, Paper } from '@mui/material';
import { useDispatch } from 'react-redux';
import { RememberDevicePrompt } from '@summa/mfa-login';
import QixitAuthPanel from '@shared/auth/QixitAuthPanel';
import { resolveReferralFromContext } from '@shared/auth/attribution';
import type { QixitAuthMeta, QixitAuthSuccess } from '@shared/auth/types';
import { clearStoredReferral } from '@shared/referralAttribution';
import { useAuth } from '../../hooks/useAuth';
import ForgotPasswordDialog from './ForgotPasswordDialog';
import { authService } from '../../services/authService';
import apiService from '../../services/api';
import { mailboxAuthApi } from '../../services/qixitAuthApi';
import { finishMailboxSignIn, isTrustedReturnUrl } from '../../services/finishMailboxSignIn';
import { clearUser, clearError, applyAuthenticatedUser } from '../../store/authSlice';
import type { AppDispatch } from '../../store/store';
import { getOrCreateFingerprintHash } from '../../utils/fingerprint';

const Login: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Checkout and other deep links send the user through login; honour where they were headed.
  const returnUrl = searchParams.get('returnUrl')?.trim() || '';
  const { isAuthenticated } = useAuth();
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordPrefill, setForgotPasswordPrefill] = useState('');
  const [pendingRemember, setPendingRemember] = useState<{
    result: QixitAuthSuccess;
    meta: QixitAuthMeta;
  } | null>(null);
  const [rememberLoading, setRememberLoading] = useState(false);
  // The prompt's close event can fire twice (button click, then dialog unmount).
  const finishingRef = useRef(false);

  const attribution = useMemo(() => {
    const referral = resolveReferralFromContext(searchParams);
    return { referralCode: referral.referralCode, sourcePage: 'mailbox-login' };
  }, [searchParams]);

  React.useEffect(() => {
    // Clear stale sessions before sign-in. Keep tokens while the post-MFA
    // prompt is open, and after the user leaves it, until the session is stored.
    if (isAuthenticated || pendingRemember || finishingRef.current) return;
    apiService.clearTokens();
    localStorage.removeItem('user');
    dispatch(clearUser());
    dispatch(clearError());
  }, [dispatch, isAuthenticated, pendingRemember]);

  const goAfterLogin = (meta: QixitAuthMeta) => {
    if (returnUrl && isTrustedReturnUrl(returnUrl)) {
      if (returnUrl.startsWith('/')) navigate(returnUrl);
      else window.location.href = returnUrl;
      return;
    }
    navigate(meta.isNewAccount ? '/profile?tab=account-settings' : '/messages');
  };

  const complete = async (result: QixitAuthSuccess, meta: QixitAuthMeta) => {
    const user = await finishMailboxSignIn(result);
    if (meta.isNewAccount) clearStoredReferral();
    dispatch(applyAuthenticatedUser(user));
    goAfterLogin(meta);
  };

  const handleAuthenticated = async (result: QixitAuthSuccess, meta: QixitAuthMeta) => {
    if (meta.method === 'mfa') {
      // Tokens are needed for the remember-device call before the session is finalized.
      apiService.setTokens(result.token, result.refreshToken || '');
      setPendingRemember({ result, meta });
      return;
    }
    await complete(result, meta);
  };

  const finishAfterRememberPrompt = () => {
    const pending = pendingRemember;
    if (!pending || finishingRef.current) return;
    finishingRef.current = true;
    // Keep the prompt mounted until the session is stored. Dropping it first
    // re-runs the stale-session clear and wipes the tokens just issued by MFA.
    void (async () => {
      try {
        await complete(pending.result, pending.meta);
      } catch (err) {
        console.error('Sign-in after MFA failed:', err);
        finishingRef.current = false;
      } finally {
        setPendingRemember(null);
      }
    })();
  };

  if (pendingRemember) {
    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <RememberDevicePrompt
            open
            loading={rememberLoading}
            onContinue={finishAfterRememberPrompt}
            onRemember={async () => {
              setRememberLoading(true);
              try {
                await authService.rememberDevice({
                  fingerprint: getOrCreateFingerprintHash(),
                  scopeCredentialId: pendingRemember.meta.mfaScopeCredentialId || undefined,
                });
              } catch (err) {
                console.warn('Failed to remember device:', err);
              } finally {
                setRememberLoading(false);
              }
            }}
          />
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }} data-testid="mailbox-login-page">
          <QixitAuthPanel
            api={mailboxAuthApi}
            context="mailbox"
            variant="page"
            testIdPrefix="mailbox"
            defaultMode="login"
            title="Log in or create your Qixit account"
            subtitle="New? Enter your email* or continue with Google or Microsoft to create an account."
            returnUrl={typeof window !== 'undefined' ? window.location.href : undefined}
            attribution={attribution}
            onForgotPassword={(identifier) => {
              setForgotPasswordPrefill(identifier.includes('@') ? '' : identifier);
              setForgotPasswordOpen(true);
            }}
            onAuthenticated={handleAuthenticated}
          />
        </Paper>
      </Box>
      <ForgotPasswordDialog
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
        prefillUsername={forgotPasswordPrefill}
      />
    </Container>
  );
};

export default Login;
